import { types as api } from '@mesa-labs/mesa-api';
import { DateTime } from 'luxon';

import { parseStoredSavedFilters, SavedFilter } from './savedFilters';

export type NumberRange = {
  readonly low?: number;
  readonly high?: number;
};

/** Inclusive calendar-date bounds as yyyy-MM-dd strings (JSON-safe for persisted and saved filters). */
export type DateRange = {
  readonly after?: string;
  readonly before?: string;
};

/**
 * The Upsell scope lists inactive vendor programs at a partner other than the vendor's onboarding
 * partner. It is a UI-only scope: it reads from its own endpoint (upsellStatusSummary) rather than
 * being an api.EnablementV2Scope value.
 */
export const UpsellScope = 'upsell';
export type EnablementV2ViewScope = api.EnablementV2Scope | typeof UpsellScope;

export const isUpsellScope = (scope: EnablementV2ViewScope): scope is typeof UpsellScope => scope === UpsellScope;

/** Sales Owner filter option for vendors with no sales owner (sent as includeUnassignedSalesOwner). */
export const UnassignedSalesOwner = 'unassigned';
export type EnablementV2SalesOwnerFilterValue = api.SalesOwner | typeof UnassignedSalesOwner;

/**
 * Partner filter shortcuts for every partner of a collection-scheme type. Stored as the group (not
 * its members) so saved filters pick up partners added to mesa-api's partnerConfig later.
 */
export const DirectDebitPartnerGroup = 'type:direct-debit';
export const FlowOfFundsPartnerGroup = 'type:flow-of-funds';
export type EnablementV2PartnerGroup = typeof DirectDebitPartnerGroup | typeof FlowOfFundsPartnerGroup;
export type EnablementV2PartnerFilterValue = api.Partners | EnablementV2PartnerGroup;

const isPartnerGroup = (value: string | number): value is EnablementV2PartnerGroup => (
  value === DirectDebitPartnerGroup || value === FlowOfFundsPartnerGroup
);

// Matches vendor-service's DIRECT_DEBIT_COLLECTIONS_PARTNER_IDS: partner factoring counts as direct debit.
const isDirectDebitPartner = (partner: api.Partners): boolean => [
  api.InvoiceCollectionScheme.DIRECT_DEBIT,
  api.InvoiceCollectionScheme.PARTNER_FACTORING,
].includes(api.getPartnerCollectionScheme(partner));

const AllPartners = Object.values(api.Partners).filter((value): value is api.Partners => typeof value === 'number');

export const partnersInGroup = (group: EnablementV2PartnerGroup): readonly api.Partners[] => (
  AllPartners.filter((partner) => isDirectDebitPartner(partner) === (group === DirectDebitPartnerGroup))
);

/** Expands partner groups into their member partners, de-duplicated. */
export const expandPartnerFilter = (values: readonly EnablementV2PartnerFilterValue[]): readonly api.Partners[] => [
  ...new Set(values.flatMap((value) => (isPartnerGroup(value) ? partnersInGroup(value) : [value]))),
];

/**
 * Multi-select column filters for the Enablement V2 vendor table. Values within a column are OR'd;
 * populated columns are AND'd with each other and with the scope (server-side).
 */
export type EnablementV2ColumnFilters = {
  readonly queues: readonly api.EnablementV2Queue[];
  readonly assignmentStatuses: readonly api.EnablementV2AssignmentStatus[];
  // In the Upsell scope this is the program's (target) partner.
  readonly partners: readonly EnablementV2PartnerFilterValue[];
  // Upsell scope only: the vendor's onboarding partner.
  readonly onboardingPartners: readonly EnablementV2PartnerFilterValue[];
  readonly salesOwners: readonly EnablementV2SalesOwnerFilterValue[];
  readonly vendorActionsStatuses: readonly api.EnablementV2VendorActionsStatus[];
  readonly kybKycStatuses: readonly api.EnablementV2KybKycStatus[];
  readonly underwritingStatuses: readonly api.EnablementV2StepStatus[];
  readonly underwritingStages: readonly api.VendorUnderwritingStage[];
  readonly underwritingPriorities: readonly api.EnablementV2UnderwritingPriority[];
  // Age is stored relative (days since creation — of the vendor, or of the program in the Upsell
  // scope) so a saved filter like "older than 30 days" stays meaningful.
  readonly ageDays?: NumberRange;
  // Absolute calendar dates (e.g. a marketing campaign's dates) on the same created date as Age. It
  // coexists with Age; the two are intersected into the single created-at window the API takes.
  readonly createdAt?: DateRange;
  readonly spend?: NumberRange;
  // Single-valued (unlike the option filters): vendors with an open Action Item, one assigned to me, or none.
  // Vendor-level, so in the Upsell scope it matches every program of a matching vendor.
  readonly vendorActionItems?: api.VendorActionItemQueueFilter;
  // Single-valued: Corrigo/Tungsten vendors (Upsell: target partner) that have (true) or haven't (false)
  // imported invoice history. Either value hides other partners.
  readonly historicalInvoicesImported?: boolean;
};

export type EnablementV2OptionFilterKey = Exclude<keyof EnablementV2ColumnFilters, 'ageDays' | 'createdAt' | 'spend' | 'vendorActionItems' | 'historicalInvoicesImported'>;
export type EnablementV2RangeFilterKey = 'ageDays' | 'spend';

export const EmptyColumnFilters: EnablementV2ColumnFilters = {
  queues: [],
  assignmentStatuses: [],
  partners: [],
  onboardingPartners: [],
  salesOwners: [],
  vendorActionsStatuses: [],
  kybKycStatuses: [],
  underwritingStatuses: [],
  underwritingStages: [],
  underwritingPriorities: [],
  ageDays: undefined,
  createdAt: undefined,
  spend: undefined,
  vendorActionItems: undefined,
  historicalInvoicesImported: undefined,
};

export const OptionFilterKeys: readonly EnablementV2OptionFilterKey[] = [
  'queues',
  'assignmentStatuses',
  'partners',
  'onboardingPartners',
  'salesOwners',
  'vendorActionsStatuses',
  'kybKycStatuses',
  'underwritingStatuses',
  'underwritingStages',
  'underwritingPriorities',
];

// Partners are numeric enum values (or a group shortcut); every other option filter is a string enum.
const parseOptionValue = (key: EnablementV2OptionFilterKey, value: string): string | number => (
  (key === 'partners' || key === 'onboardingPartners') && !isPartnerGroup(value) ? Number(value) : value
);

export const addColumnFilterValue = (
  filters: EnablementV2ColumnFilters,
  key: EnablementV2OptionFilterKey,
  value: string,
): EnablementV2ColumnFilters => {
  const parsed = parseOptionValue(key, value);
  const current = filters[key] as readonly (string | number)[];
  return current.includes(parsed) ? filters : { ...filters, [key]: [...current, parsed] };
};

export const removeColumnFilterValue = (
  filters: EnablementV2ColumnFilters,
  key: EnablementV2OptionFilterKey,
  value: string,
): EnablementV2ColumnFilters => {
  const parsed = parseOptionValue(key, value);
  return { ...filters, [key]: (filters[key] as readonly (string | number)[]).filter((v) => v !== parsed) };
};

export const isRangeSet = (range: NumberRange | undefined): range is NumberRange => (
  range !== undefined && (range.low !== undefined || range.high !== undefined)
);

/** Normalizes an empty range (both bounds cleared) to undefined so it doesn't count as an active filter. */
export const toRange = (low: number | undefined, high: number | undefined): NumberRange | undefined => (
  low !== undefined || high !== undefined ? { low, high } : undefined
);

export const isDateRangeSet = (range: DateRange | undefined): range is DateRange => (
  range !== undefined && (range.after !== undefined || range.before !== undefined)
);

/** Normalizes empty date bounds ('' from a cleared date input) to undefined, and an empty range to undefined. */
export const toDateRange = (after: string | undefined, before: string | undefined): DateRange | undefined => (
  after || before ? { after: after || undefined, before: before || undefined } : undefined
);

export const countActiveColumnFilters = (filters: EnablementV2ColumnFilters): number => (
  OptionFilterKeys.reduce((sum, key) => sum + filters[key].length, 0)
  + (isRangeSet(filters.ageDays) ? 1 : 0)
  + (isDateRangeSet(filters.createdAt) ? 1 : 0)
  + (isRangeSet(filters.spend) ? 1 : 0)
  + (filters.vendorActionItems ? 1 : 0)
  + (filters.historicalInvoicesImported !== undefined ? 1 : 0)
);

const nonEmpty = <T>(values: readonly T[]): readonly T[] | undefined => (values.length > 0 ? values : undefined);

const latest = (...dates: readonly (DateTime | undefined)[]): DateTime | undefined => (
  dates.reduce<DateTime | undefined>((max, d) => (d !== undefined && (max === undefined || d > max) ? d : max), undefined)
);

const earliest = (...dates: readonly (DateTime | undefined)[]): DateTime | undefined => (
  dates.reduce<DateTime | undefined>((min, d) => (d !== undefined && (min === undefined || d < min) ? d : min), undefined)
);

/**
 * Converts column filters to API params. An age of N..M days maps to a created-at window of
 * [now - M days, now - N days]; Created At after A / before B maps to [start of A, end of B]. The
 * API takes one created-at window, so when both are set it gets their intersection.
 */
export const toColumnFilterParams = (
  filters: EnablementV2ColumnFilters,
  now: DateTime = DateTime.now(),
): Partial<api.VendorStatusSummaryFilterParams> => {
  const createdBeginningAt = latest(
    filters.ageDays?.high !== undefined ? now.minus({ days: filters.ageDays.high }).startOf('day') : undefined,
    filters.createdAt?.after ? DateTime.fromISO(filters.createdAt.after).startOf('day') : undefined,
  );
  const createdEndingAt = earliest(
    filters.ageDays?.low !== undefined ? now.minus({ days: filters.ageDays.low }).endOf('day') : undefined,
    filters.createdAt?.before ? DateTime.fromISO(filters.createdAt.before).endOf('day') : undefined,
  );
  return {
    queues: nonEmpty(filters.queues),
    assignmentStatuses: nonEmpty(filters.assignmentStatuses),
    partners: nonEmpty(expandPartnerFilter(filters.partners)),
    salesOwners: nonEmpty(filters.salesOwners.filter((owner): owner is api.SalesOwner => owner !== UnassignedSalesOwner)),
    includeUnassignedSalesOwner: filters.salesOwners.includes(UnassignedSalesOwner) || undefined,
    vendorActionsStatuses: nonEmpty(filters.vendorActionsStatuses),
    kybKycStatuses: nonEmpty(filters.kybKycStatuses),
    underwritingStatuses: nonEmpty(filters.underwritingStatuses),
    underwritingStages: nonEmpty(filters.underwritingStages),
    underwritingPriorities: nonEmpty(filters.underwritingPriorities),
    createdBeginningAt: createdBeginningAt?.toISO() as api.ISODateString | undefined,
    createdEndingAt: createdEndingAt?.toISO() as api.ISODateString | undefined,
    spendLow: filters.spend?.low,
    spendHigh: filters.spend?.high,
    vendorActionItems: filters.vendorActionItems,
    historicalInvoicesImported: filters.historicalInvoicesImported,
  };
};

const formatBounds = <T>(low: T | undefined, high: T | undefined, format: (bound: T) => string): string => {
  if (low !== undefined && high !== undefined) return `${format(low)} – ${format(high)}`;
  if (low !== undefined) return `≥ ${format(low)}`;
  return `≤ ${format(high as T)}`;
};

// Chip value text; FilterBar prefixes the category label ("Age: older than 30 days").
// Age reads in plain words: a minimum age is "older than", a maximum is "newer than".
export const formatAgeRange = ({ low, high }: NumberRange): string => {
  if (low !== undefined && high !== undefined) return `${low} – ${high} days old`;
  if (low !== undefined) return `older than ${low} days`;
  return `newer than ${high as number} days`;
};

export const formatSpendRange = (range: NumberRange): string => formatBounds(range.low, range.high, (n) => `$${n.toLocaleString('en-US')}`);

export const formatCreatedAtRange = (range: DateRange): string => formatBounds(
  range.after,
  range.before,
  (date) => DateTime.fromISO(date).toLocaleString(DateTime.DATE_MED),
);

/**
 * Back-fills column filter keys missing from older persisted or saved state (e.g. a column added
 * after the state was written), so callers can always index every key.
 */
export const withColumnFilterDefaults = (
  filters: Partial<EnablementV2ColumnFilters> | undefined,
): EnablementV2ColumnFilters => ({ ...EmptyColumnFilters, ...filters });

// ─── Filter sets (scope + search + column filters) ──────────────────────────

export type EnablementV2FilterSet = {
  readonly scope: EnablementV2ViewScope;
  readonly search?: string;
  readonly columnFilters: EnablementV2ColumnFilters;
};

/** The bucket-like part of a filter set that a single legacy EnablementFilter status expressed. */
export type EnablementV2BucketFocus = {
  readonly scope: EnablementV2ViewScope;
  readonly columnFilters: Partial<EnablementV2ColumnFilters>;
};

/**
 * Translates a legacy EnablementFilter status (the old bucket dropdown, still in persisted state
 * and saved filters) into the scope + column filters that match the same vendors.
 */
export const fromLegacyStatus = (status: api.EnablementFilter | undefined): EnablementV2BucketFocus => {
  const inEnablement = (columnFilters: Partial<EnablementV2ColumnFilters> = {}): EnablementV2BucketFocus => ({
    scope: api.EnablementV2Scope.InEnablement,
    columnFilters,
  });
  switch (status) {
    case api.EnablementFilter.AWAITING_PARTNER_ENABLEMENT:
      return { scope: api.EnablementV2Scope.PartnerEnablement, columnFilters: {} };
    case api.EnablementFilter.READY_FOR_ACTIVATION:
      return inEnablement({ queues: [api.EnablementV2Queue.ReadyToActivate] });
    case api.EnablementFilter.REQ_APPROVAL:
      return inEnablement({ queues: [api.EnablementV2Queue.ReqApproval] });
    case api.EnablementFilter.UW_IN_REVIEW:
      return inEnablement({ queues: [api.EnablementV2Queue.UwInReview] });
    case api.EnablementFilter.REQUIRES_UNDERWRITING:
      return inEnablement({ queues: [api.EnablementV2Queue.RequiresUnderwriting] });
    case api.EnablementFilter.OD_IN_FLIGHTS:
      return inEnablement({ queues: [api.EnablementV2Queue.OdInFlights] });
    case api.EnablementFilter.VENDOR_ACTION:
      return inEnablement({ vendorActionsStatuses: [api.EnablementV2VendorActionsStatus.Incomplete] });
    case api.EnablementFilter.VENDOR_ACTIONS_COMPLETE:
      return inEnablement({ vendorActionsStatuses: [api.EnablementV2VendorActionsStatus.Complete] });
    case api.EnablementFilter.REQUIRES_ASSIGNMENT:
      return inEnablement({ assignmentStatuses: [api.EnablementV2AssignmentStatus.Unassigned] });
    case api.EnablementFilter.DIRECT_DEBIT_PARTNERS:
      return inEnablement({ partners: [DirectDebitPartnerGroup] });
    case api.EnablementFilter.FLOW_OF_FUNDS_PARTNERS:
      return inEnablement({ partners: [FlowOfFundsPartnerGroup] });
    default: // ALL_IN_ENABLEMENT, and buckets that were never offered in the dropdown
      return inEnablement();
  }
};

/** Vendor-level KYB / underwriting columns, which the Upsell (per-program) table doesn't have. */
export const UpsellUnsupportedFilterKeys: readonly EnablementV2OptionFilterKey[] = [
  'kybKycStatuses',
  'underwritingStatuses',
  'underwritingStages',
  'underwritingPriorities',
];

/** Filters only the Upsell scope has. */
export const UpsellOnlyFilterKeys: readonly EnablementV2OptionFilterKey[] = ['onboardingPartners'];

/** Option filter keys that don't apply to the scope, and so are hidden and cleared. */
export const unsupportedFilterKeys = (scope: EnablementV2ViewScope): readonly EnablementV2OptionFilterKey[] => {
  if (isUpsellScope(scope)) return UpsellUnsupportedFilterKeys;
  // Queues are In Enablement workflow stages, so they never apply to Partner Enablement (activated
  // vendors). vendor-service rejects that combination.
  return scope === api.EnablementV2Scope.PartnerEnablement ? [...UpsellOnlyFilterKeys, 'queues'] : UpsellOnlyFilterKeys;
};

/** Drops filters that contradict the scope (see unsupportedFilterKeys). */
export const withoutScopeConflicts = (filters: EnablementV2FilterSet): EnablementV2FilterSet => {
  const conflicting = unsupportedFilterKeys(filters.scope).filter((key) => filters.columnFilters[key].length > 0);
  return conflicting.length > 0
    ? {
      ...filters,
      columnFilters: conflicting.reduce((columnFilters, key) => ({ ...columnFilters, [key]: [] }), filters.columnFilters),
    }
    : filters;
};

/** Shape of a filter set as persisted before or after the move from `status` to scope + queues. */
export type StoredEnablementV2FilterSet = {
  readonly scope?: EnablementV2ViewScope;
  readonly status?: api.EnablementFilter;
  readonly search?: string;
  readonly columnFilters?: Partial<EnablementV2ColumnFilters>;
};

const union = <T>(a: readonly T[], b: readonly T[] | undefined): readonly T[] => [...new Set([...a, ...(b ?? [])])];

/**
 * Normalizes a stored filter set: back-fills missing column keys and, when it predates `scope`,
 * folds its legacy `status` into scope + column filters.
 */
export const normalizeFilterSet = (stored: StoredEnablementV2FilterSet): EnablementV2FilterSet => {
  const columnFilters = withColumnFilterDefaults(stored.columnFilters);
  if (stored.scope !== undefined) {
    return withoutScopeConflicts({ scope: stored.scope, search: stored.search, columnFilters });
  }
  const focus = fromLegacyStatus(stored.status);
  return withoutScopeConflicts({
    scope: focus.scope,
    search: stored.search,
    columnFilters: OptionFilterKeys.reduce((merged, key) => ({
      ...merged,
      [key]: union(merged[key] as readonly unknown[], focus.columnFilters[key] as readonly unknown[] | undefined),
    }), columnFilters),
  });
};

export const toVendorStatusSummaryParams = (
  filters: EnablementV2FilterSet,
  now: DateTime = DateTime.now(),
): Partial<api.VendorStatusSummaryFilterParams> => ({
  scope: isUpsellScope(filters.scope) ? undefined : filters.scope,
  search: filters.search || undefined,
  ...toColumnFilterParams(filters.columnFilters, now),
});

/**
 * Converts a filter set to Upsell list params. The Partner filter is the program's (target) partner,
 * and Age and Created At map to a program-created window (see toColumnFilterParams).
 */
export const toUpsellStatusSummaryParams = (
  filters: EnablementV2FilterSet,
  now: DateTime = DateTime.now(),
): Partial<api.VendorUpsellStatusSummaryFilterParams> => {
  const { columnFilters } = filters;
  const { createdBeginningAt, createdEndingAt, salesOwners, includeUnassignedSalesOwner, spendLow, spendHigh, vendorActionItems, historicalInvoicesImported } = toColumnFilterParams(columnFilters, now);
  return {
    search: filters.search || undefined,
    targetPartners: nonEmpty(expandPartnerFilter(columnFilters.partners)),
    onboardingPartners: nonEmpty(expandPartnerFilter(columnFilters.onboardingPartners)),
    queues: nonEmpty(columnFilters.queues),
    assignmentStatuses: nonEmpty(columnFilters.assignmentStatuses),
    vendorActionsStatuses: nonEmpty(columnFilters.vendorActionsStatuses),
    salesOwners,
    includeUnassignedSalesOwner,
    programCreatedBeginningAt: createdBeginningAt,
    programCreatedEndingAt: createdEndingAt,
    spendLow,
    spendHigh,
    vendorActionItems,
    historicalInvoicesImported,
  };
};

// ─── Saved filters (per user, localStorage only) ────────────────────────────

export type EnablementV2SavedFilter = SavedFilter<EnablementV2FilterSet>;

export const savedFiltersStorageKey = (email: string | undefined): string => (
  `mesa.enablementV2.savedFilters.v1:${email ?? 'anonymous'}`
);

/**
 * Parses stored saved filters, tolerating corrupt JSON and normalizing filters saved in an older
 * shape (missing column keys, or a legacy `status` bucket).
 */
export const parseSavedFilters = (raw: string | null): readonly EnablementV2SavedFilter[] => (
  parseStoredSavedFilters(raw, (stored: StoredEnablementV2FilterSet) => normalizeFilterSet(stored))
);
